/**
 * Module dependencies.
 */

import { Image, RouterLink, Svg, icons } from 'src/components/core';
import { ReactElement } from 'react';
import { Type, color, units } from '@untile/react-components';
import { prop } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  bgColor: string;
  className?: string;
  description: string;
  imageUrl: string;
  slug: string;
  title: string;
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  transition: transform 0.3s ease;
  will-change: transform;
`;

/**
 * `TextWrapper` styled component.
 */

const TextWrapper = styled.div`
  display: grid;
  grid-area: textWrapper;
  grid-template-areas:
    'title'
    'description'
    'icon';
  grid-template-columns: 1fr;
  grid-template-rows: max-content 1fr max-content;
  padding: ${units(2)};
  transition: transform 0.4s ease;
  will-change: transform;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg)`
  grid-area: icon;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)`
  color: ${color('textColor')};
  display: grid;
  grid-template-areas:
    'image'
    'textWrapper';
  grid-template-columns: 1fr;
  grid-template-rows: max-content 1fr;
  height: 100%;
  outline: none;
  position: relative;
  text-decoration: none;
  transition: transform 0.3s ease;
  will-change: transform;
  z-index: 1;

  &:focus,
  &:hover {
    transform: scale(0.98);

    ${StyledImage} {
      transform: scale(0.98);
    }

    ${TextWrapper} {
      transform: scale(0.965);
    }
  }
`;

/**
 * `ImageBg` styled component.
 */

const ImageBg = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  grid-area: image;
  padding-bottom: 100%;
  position: relative;
`;

/**
 * `ImageInnerWrapper` styled component.
 */

const ImageInnerWrapper = styled.div`
  height: 100%;
  padding: ${units(3)} 0;
  position: absolute;
  width: 100%;
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

/**
 * `TechnologyCard` component.
 */

const TechnologyCard = (props: Props): ReactElement => {
  const { bgColor, className, description, imageUrl, slug, title } = props;
  const { locale } = useTranslate();
  const routeResolver = useLocalizedRoute();

  return (
    <Card
      className={className}
      href={routeResolver('technologyDetails', { slug })}
      locale={locale}
    >
      <ImageBg bgColor={bgColor}>
        <ImageInnerWrapper>
          <ImageWrapper>
            <StyledImage
              alt={title}
              fill
              src={imageUrl}
              style={{ objectFit: 'contain' }}
            />
          </ImageWrapper>
        </ImageInnerWrapper>
      </ImageBg>

      <TextWrapper>
        <Type.H3
          gridArea={'title'}
          marginBottom={'10px'}
          marginBottomMs={units(2)}
        >
          {title}
        </Type.H3>

        <Type.Paragraph
          gridArea={'description'}
          marginBottom={units(1)}
        >
          {description}
        </Type.Paragraph>

        <Icon
          icon={icons.arrowRightLarge}
          size={units(6)}
        />
      </TextWrapper>
    </Card>
  );
};

/**
 * Export `TechnologyCard` component.
 */

export default TechnologyCard;
